/** Shared NEW / 2ND / 3RD customer order badges (phone history). */

export function formatOrdinal(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num < 1) return "";
  const mod100 = num % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${num}th`;
  const mod10 = num % 10;
  if (mod10 === 1) return `${num}st`;
  if (mod10 === 2) return `${num}nd`;
  if (mod10 === 3) return `${num}rd`;
  return `${num}th`;
}

function orderSequenceNumber(order) {
  const num = Number(order?.customer_order_number);
  return Number.isFinite(num) && num >= 1 ? num : null;
}

/** True when M would show NEW CUSTOMER (first order by phone history). */
export function isNewCustomerOrder(order) {
  if (order?.is_new_customer != null) return Boolean(order.is_new_customer);
  const seq = orderSequenceNumber(order);
  if (seq != null) return seq === 1;
  if (order?.customer_repeat != null) return !Boolean(order.customer_repeat);
  // Unknown phone history — do not treat as NEW (avoids accidental free goodies).
  return false;
}

/** Does the free-gift step apply to this production order? (TT-861)
 *
 * The server owns this answer and ships it as `is_new_customer` on the task
 * payload — computed by the same predicate the Decorator Done gate uses, after
 * customer-history enrichment. The phone must not re-derive it: it is not given
 * the raw history, and guessing produced a bright Done button that the server
 * rejected with "Mark free first" for every new customer.
 *
 * Unknown resolves to NEW, matching `is_new_customer_order` on the server. The
 * failure mode is then a Free pill the decorator can tap, never a dead button.
 */
export function decoratorRequiresFreeGift(order) {
  if (!order) return false;
  if (order.is_new_customer != null) return Boolean(order.is_new_customer);
  const seq = orderSequenceNumber(order);
  if (seq != null) return seq === 1;
  if (order.customer_repeat != null) return !toBadgeBool(order.customer_repeat);
  return true;
}

/** API booleans are sometimes stringified; Boolean("false") is true. */
function toBadgeBool(v) {
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return Boolean(v);
}

export function getCustomerOrderBadge(order, { compact = false } = {}) {
  const seq = orderSequenceNumber(order);
  const isRepeat = Boolean(order?.customer_repeat);
  const isFirst = seq === 1 || (!seq && !isRepeat);

  let label;
  if (isFirst) {
    label = compact ? "NEW" : "NEW CUSTOMER";
  } else if (seq >= 2) {
    const ordinal = formatOrdinal(seq).toUpperCase();
    label = compact ? ordinal : `${ordinal} ORDER`;
  } else if (isRepeat) {
    label = compact ? "REPEAT" : "REPEAT CUSTOMER";
  } else {
    label = compact ? "NEW" : "NEW CUSTOMER";
  }

  const title = seq
    ? `${isFirst ? "First" : `${formatOrdinal(seq)}`} order by phone history`
    : "Customer type based on phone history";

  return {
    label,
    isRepeat: !isFirst,
    title,
    className: isFirst
      ? "bg-green-600 text-white border-green-700"
      : "bg-indigo-600 text-white border-indigo-700",
  };
}
